'use client';

import { FC, useRef, useEffect } from 'react';

interface VideoPlayerProps {
    videoUrl?: string;
    title?: string;
    onDurationLoaded?: (duration: string) => void;
    playRequest?: number;
}

/**
 * Format seconds to MM:SS string
 */
const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Check if URL is a YouTube embed URL
 */
const isYouTubeUrl = (url: string): boolean => {
    return url.includes('youtube.com') || url.includes('youtu.be');
};

const VideoPlayer: FC<VideoPlayerProps> = ({ videoUrl, title, onDurationLoaded, playRequest = 0 }) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !videoUrl || isYouTubeUrl(videoUrl)) return;

        const handleMetadata = () => {
            if (video.duration && !isNaN(video.duration) && onDurationLoaded) {
                onDurationLoaded(formatDuration(video.duration));
            }
        };

        video.addEventListener('loadedmetadata', handleMetadata);

        // If metadata already loaded (cached video)
        if (video.readyState >= 1) {
            handleMetadata();
        }

        return () => {
            video.removeEventListener('loadedmetadata', handleMetadata);
        };
    }, [videoUrl, onDurationLoaded]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !videoUrl || !playRequest || isYouTubeUrl(videoUrl)) return;

        // Lesson rows use a play icon, so honor the user's tap by attempting playback.
        // Browsers can still decline autoplay; the player remains in view with controls.
        void video.play().catch(() => {});
    }, [playRequest, videoUrl]);

    if (!videoUrl) {
        return (
            <div className='relative w-full aspect-video overflow-hidden bg-gray-900 flex items-center justify-center'>
                <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-200">No Video Available</h3>
                </div>
            </div>
        );
    }

    // YouTube embed
    if (isYouTubeUrl(videoUrl)) {
        return (
            <div className='relative w-full aspect-video overflow-hidden bg-gray-900'>
                <iframe
                    src={videoUrl}
                    title={title || 'Tutorial Video'}
                    className='absolute top-0 left-0 w-full h-full'
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                    allowFullScreen
                />
            </div>
        );
    }

    // Local/native video - use playbooks-video class to override global video control hiding
    return (
        <div className='relative w-full aspect-video overflow-hidden bg-black'>
            <video
                ref={videoRef}
                src={videoUrl}
                title={title || 'Tutorial Video'}
                className='playbooks-video w-full h-full'
                controls
                controlsList="nodownload"
                preload="metadata"
                playsInline
                autoPlay={playRequest > 0}
                style={{ backgroundColor: '#000' }}
            />
        </div>
    );
};

export default VideoPlayer;
